import React, { createContext, FC, PropsWithChildren, useContext, useMemo, useState } from 'react';

import { CheckHeader } from 'Src/feature/check/models';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { TabSelectOption } from 'Src/feature/form/FormFieldTabSelect';
import { useRevenueCenters } from 'Src/feature/revenue-center/useRevenueCenter';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { routeParamValue } from 'Src/routes/params';
import useRoute from 'Src/routes/useRoute';

type OrderKindId = 'current' | 'scheduled';
type CurrentSelection = OrderKindId | CheckHeader['revenueCenterId'] | 'searchMode';

const useChecksPageStore = () => {
  const { navigate, crumbs, route } = useRoute();
  const revenueCentersAll = useRevenueCenters.query.getAll();
  const revenueCentersForTerminal = useRevenueCenters.query.currentForTerminal();
  const terminalLocation = useTerminal.query.get();
  const checksAll = useCheckHeader.query.getAll();

  const [currentSelection, setCurrentSelection] = useState<CurrentSelection>('current');
  const [prevTabId, setPrevTabId] = useState<typeof route.params.active.tabId>(undefined);

  const terminalLocationId = terminalLocation.data?.location.id;
  const revenueCenterOptions = useMemo(() => (revenueCentersAll.data || [])
    // appending a position property from terminal location
    .map((rc) => {
      const revenueLocation = rc.locations.find(({ id }) => id === terminalLocationId);
      return {
        ...rc,
        position: revenueLocation
        // TODO: remove casting when type is available
          ? (revenueLocation as unknown as { position: number }).position
          : -1,
      };
    })
    // sort on random position number
    .sort((a, b) => a.position - b.position)
    // converting to options
    .map(({ id, name }) => ({ value: id, label: name })),
  [revenueCentersAll.data, terminalLocationId]);

  const numScheduledChecks = useMemo(() => checksAll.data ? checksAll.data.items.filter((chk) => !!chk.scheduledAt).length : 0, [checksAll.data]);
  const orderKindOptions = useMemo(() => [{ value: 'current', label: 'Current Orders' }, { value: 'scheduled', label: `${numScheduledChecks} Scheduled Orders` }], [numScheduledChecks]);

  // This may look weird... setting state during a render?
  // This is the suggested method when you have a state that needs to change after a prop, or in this case another state (the route) changes.
  // See this https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (route.params.active.tabId !== prevTabId) {
    setPrevTabId(route.params.active.tabId);
    switch (route.params.active.tabId) {
      case route.params.value.checksList:
        setCurrentSelection('current');
        break;
      case route.params.value.checksFloorPlan:
        setCurrentSelection(revenueCentersForTerminal.data ? revenueCentersForTerminal.data.id : -1);
        break;
      default:
        setCurrentSelection('current'); // default to "Current Orders", should never happen
    }
  }

  let options: TabSelectOption[] = revenueCenterOptions;
  switch (route.params.active.tabId) {
    case route.params.value.checksList:
      options = orderKindOptions;
      break;
    case route.params.value.checksFloorPlan:
      options = revenueCenterOptions;
      break;
    default:
      options = revenueCenterOptions;
  }

  return {
    currentSelection,
    setCurrentSelection,
    options,
    section: {
      value: route.params.active.tabId,
      options: [
        { value: routeParamValue.checksFloorPlan, label: 'Tables' },
        { value: routeParamValue.checksList, label: 'Checks' },
      ],
      onChange: (nextTabId: string) => {
        navigate(crumbs.pos.checks.main.interpolate({ tabId: nextTabId }));
      },
    },
  };
};

type ChecksPageStore = ReturnType<typeof useChecksPageStore>;


const ChecksPageContext = createContext<ChecksPageStore | undefined>(undefined);
ChecksPageContext.displayName = 'ChecksPageContext';

const useChecksPage = () => {
  const store = useContext(ChecksPageContext);
  // this is especially useful in TypeScript, so you don't need to be checking for null all the time
  if (!store) throw new Error('useChecksPage must be used within a ChecksPageContext');
  return store;
};

const ChecksPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const store = useChecksPageStore();
  return (
    <ChecksPageContext.Provider value={store}>{children}</ChecksPageContext.Provider>
  );
};

export { ChecksPageProvider, useChecksPage };
